import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Room, RoomSession } from '../../models/Room'
import { RootState } from '../store'
import { formatDateDDMMYYYY, formatDateHM, getUserDisplayName } from '../../apps/professional-app/fa/common/aifeatures/utils'

interface RoomSessionState {
  isLoading: boolean
  roomSession: RoomSession | null
  room: Room | null
  hasTranscription: boolean
  isMetadataUpdateLoading: boolean
  isDeleteRoomSessionLoading: boolean
  pollingEnabled: boolean
}

const initialState: RoomSessionState = {
  isLoading: true,
  roomSession: null,
  room: null,
  hasTranscription: false,
  isMetadataUpdateLoading: false,
  isDeleteRoomSessionLoading: false,
  pollingEnabled: false,
}

export const roomSessionSlice = createSlice({
  name: 'roomSession',
  initialState,
  reducers: {
    setRoomSessionIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setRoomSession: (state, action: PayloadAction<RoomSession>) => {
      state.roomSession = action.payload
    },
    setRoom: (state, action: PayloadAction<Room>) => {
      state.room = action.payload
    },
    setRoomSessionHasTranscription: (state, action: PayloadAction<boolean>) => {
      state.hasTranscription = action.payload
    },
    updateRoomSessionMetadata: (state, action: PayloadAction<any>) => {
      if (!state.roomSession) {
        return
      }
      state.roomSession.metadata = action.payload
    },
    setRoomSessionMetadataUpdateLoading: (state, action: PayloadAction<boolean>) => {
      state.isMetadataUpdateLoading = action.payload
    },
    setRoomSessionDeleteLoading: (state, action: PayloadAction<boolean>) => {
      state.isDeleteRoomSessionLoading = action.payload
    },
    startRoomSessionPolling: (state) => {
      state.pollingEnabled = true
    },
    stopRoomSessionPolling: (state) => {
      state.pollingEnabled = false
    },
    resetRoomSession: () => initialState,
  },
})

export const {
  setRoomSessionIsLoading,
  setRoomSession,
  setRoom,
  setRoomSessionHasTranscription,
  updateRoomSessionMetadata,
  setRoomSessionMetadataUpdateLoading,
  setRoomSessionDeleteLoading,
  startRoomSessionPolling,
  stopRoomSessionPolling,
  resetRoomSession,
} = roomSessionSlice.actions

export const selectRoomSession = (state: RootState) => state.meetingSummary.roomSession.roomSession

export const selectRoomSessionMeetingTitle = createSelector([selectRoomSession], (roomSession): string => {
  if (!roomSession) {
    return ''
  }
  const metadata = parseMetadata(roomSession.metadata)

  const { external: { title = null } = {} } = metadata
  return title ?? 'Untitled Meeting'
})

export const selectMeetingInfoHTML = createSelector([selectRoomSession, selectRoomSessionMeetingTitle], (roomSession, meetingTitle): string => {
  if (!roomSession) {
    return ''
  }
  return `<p>
          <strong>${meetingTitle}</strong>
        <p>${formatDateDDMMYYYY(roomSession.startedAt)}</p>
        <p>
         ${formatDateHM(roomSession.startedAt)} - ${roomSession.finishedAt ? formatDateHM(roomSession.finishedAt) : ''}
        </p>
        <p>Participants:
        <ul>
        ${roomSession.users
          .map((u) => {
            return `<li>${getUserDisplayName(u)}</li>`
          })
          .join('')}
        </ul>
        </p>
      </p>`
})

export const selectRoomSessionIsLoading = (state: RootState) => state.meetingSummary.roomSession.isLoading

export const selectRoom = (state: RootState) => state.meetingSummary.roomSession.room

export const selectRoomSessionCreatedBy = (state: RootState) => state.meetingSummary.roomSession.room?.createdBy ?? null

export const selectRoomSessionHasTranscription = (state: RootState) => state.meetingSummary.roomSession.hasTranscription

export const selectRoomSessionMetadataUpdateIsLoading = (state: RootState) => state.meetingSummary.roomSession.isMetadataUpdateLoading

export const selectRoomSessionDeleteIsLoading = (state: RootState) => state.meetingSummary.roomSession.isDeleteRoomSessionLoading

export const selectRoomSessionStartedAt = (state: RootState) => state.meetingSummary.roomSession.roomSession?.startedAt

export const selectUsersWithClientRole = createSelector([selectRoomSession], (roomSession) => {
  if (!roomSession) {
    return []
  }
  return roomSession.users.filter((user) => {
    return user.roles.some((role) => role.name === 'Client')
  })
})

export const selectAnonymousUsers = createSelector([selectRoomSession], (roomSession) => {
  if (!roomSession) {
    return []
  }
  return roomSession.users.filter((user) => {
    return user.roles.some((role) => role.name === 'Anonymous')
  })
})

export const selectRoomSessionPollingEnabled = (state: RootState) => state.meetingSummary.roomSession.pollingEnabled

const parseMetadata = (metadata: string) => {
  if (!metadata) {
    return {}
  }
  try {
    return JSON.parse(metadata)
  } catch (e) {
    console.debug(`Could not parse metadata: ${metadata}`)
  }
  return {}
}
